import React, { useId, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { addYears, format, set as dateFnsSet } from 'date-fns';
import {
  getNewTriMetDate,
  triMetTimeZoneToUtc,
  userInTriMetTimeZone,
} from '#/shared/utils/date-time';
import { getDepartureOptionData } from '#/trip-planner/utils/date-time';
import GTM_HANDLES from '#/shared/gtm-handles';
import {
  DEPARTURE_OPTION_SELECTOR,
  DEPART_NOW_OPTION,
  DEPART_AT_OPTION,
  ARRIVE_BY_OPTION,
} from '#/shared/constants';
import DateTimeField from '#/trip-planner/components/DateTimeSelector/DateTimeField';
import * as Styled from './styled';

const TIME_PICKER_FORMAT = 'HH:mm';

function DateTimeSelector({
  initialTimestamp,
  departureOption,
  originAndDestinationSet,
  onTripDatetimeChange,
  onQueryParamChange,
  onPlanTrip,
  onSaveSettings,
  dateFormat,
}) {
  const { arriveBy, departNow } = getDepartureOptionData(departureOption);
  const intl = useIntl();

  // since the options have to be generated inside the component they need
  // to be memoized for the Select to track them between renders; without
  // that focus doesn't appear on the selected element when the menu is opened
  const travelPreferenceOptions = useMemo(
    () =>
      [DEPART_AT_OPTION, ARRIVE_BY_OPTION].map(option => ({
        value: option,
        label: intl.formatMessage({
          id: getDepartureOptionData(option).intlId,
        }),
      })),
    [intl]
  );

  const [selectedTravelPreference, setSelectedTravelPreference] = useState(() =>
    travelPreferenceOptions.find(
      entry => entry.value === (departNow ? DEPART_AT_OPTION : departureOption)
    )
  );

  // convert the trip time into the TriMet time zone since it will be
  // presented to the user
  const initialDate = initialTimestamp && getNewTriMetDate(initialTimestamp);
  const initialTime = initialDate && format(initialDate, TIME_PICKER_FORMAT);

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedTime, setSelectedTime] = useState(initialTime);

  const setSelectedDateTime = ({
    datePart = selectedDate,
    timePart = selectedTime,
  }) => {
    let selectedDateTime = null;

    if (datePart && timePart) {
      const [hours, minutes] = timePart.split(':');

      // the emitted datetime is in the TriMet time zone, convert it
      // back to UTC for use in the application code
      selectedDateTime = triMetTimeZoneToUtc(
        dateFnsSet(datePart, { hours, minutes })
      );
    }

    onTripDatetimeChange(selectedDateTime);
    onQueryParamChange({
      [DEPARTURE_OPTION_SELECTOR]: departNow
        ? DEPART_AT_OPTION
        : departureOption,
    });
  };

  const timeZoneNoticeId = useId();
  const travelPreferenceInputId = useId();
  const timeInputId = useId();
  const dateInputId = useId();

  // the current day in the TriMet timezone, this may not be the current
  // date in the client timezone
  const triMetToday = getNewTriMetDate();

  return (
    <Styled.DateTimeSelectorBody>
      <Styled.DateTimeSelectorInner>
        <DateTimeField
          labelIntlId="planner.time-preference-label"
          labelFor={travelPreferenceInputId}
        >
          <Styled.TravelPreferenceSelect
            value={selectedTravelPreference}
            onChange={option => {
              setSelectedTravelPreference(option);
              onQueryParamChange({ [DEPARTURE_OPTION_SELECTOR]: option.value });
            }}
            options={travelPreferenceOptions}
            inputId={travelPreferenceInputId}
            classNamePrefix="tps"
          />
          {!arriveBy && (
            <Styled.NowModeSwitch
              checked={departNow}
              rightText={intl.formatMessage({ id: 'planner.now-mode-label' })}
              TextContainer={Styled.NowModeSwitchLabel}
              onCheckedChange={checked =>
                onQueryParamChange({
                  [DEPARTURE_OPTION_SELECTOR]: checked
                    ? DEPART_NOW_OPTION
                    : selectedTravelPreference.value,
                })
              }
              toggleOnTextClick
            />
          )}
        </DateTimeField>
        <DateTimeField
          labelIntlId="planner.time-picker-label"
          labelFor={timeInputId}
        >
          <Styled.TimePicker
            selectedTime={initialTime}
            onTimeChange={nextTime => {
              setSelectedTime(nextTime);
              setSelectedDateTime({ timePart: nextTime });
            }}
            storedTimeFormat={TIME_PICKER_FORMAT}
            ariaDescribedBy={timeZoneNoticeId}
            inputId={timeInputId}
          />
          {!userInTriMetTimeZone() && (
            <Styled.TimeZoneNotice id={timeZoneNoticeId}>
              <FormattedMessage id="planner.time-zone-notice" />
            </Styled.TimeZoneNotice>
          )}
        </DateTimeField>
        <DateTimeField
          labelIntlId="planner.date-picker-label"
          labelFor={dateInputId}
        >
          <Styled.DatePicker
            selectedDate={initialDate}
            onDateChange={nextDate => {
              setSelectedDate(nextDate);
              setSelectedDateTime({ datePart: nextDate });
            }}
            dateFormat={dateFormat}
            fromDate={triMetToday}
            toDate={addYears(triMetToday, 1)}
            today={triMetToday}
            defaultDate={triMetToday}
            inputId={dateInputId}
          />
        </DateTimeField>
        <Styled.InputButtonsSpacer />
        {originAndDestinationSet && (
          <Styled.PlanTripButton
            variant="primary"
            onClick={onPlanTrip}
            dataGtm={GTM_HANDLES.dateTimeSettings.getDirectionsButton}
          >
            <FormattedMessage id="planner.plantrip" />
          </Styled.PlanTripButton>
        )}
        <Styled.SaveSettingsButton
          onClick={onSaveSettings}
          data-gtm={GTM_HANDLES.dateTimeSettings.saveSettingsButton}
          variant="outline"
        >
          <FormattedMessage id="planner.savechanges" />
        </Styled.SaveSettingsButton>
      </Styled.DateTimeSelectorInner>
    </Styled.DateTimeSelectorBody>
  );
}

DateTimeSelector.propTypes = {
  /** Timestamp used to set date and fields on component load */
  initialTimestamp: PropTypes.number.isRequired,
  /** Determines how selected datetime is used, as depart/arrive/now */
  departureOption: PropTypes.string.isRequired,
  /** Whether both trip end points have been set by the user */
  originAndDestinationSet: PropTypes.bool.isRequired,
  /** Format date is displayed in on date input */
  dateFormat: PropTypes.string.isRequired,
  /** Function fired when user changes date or time field */
  onTripDatetimeChange: PropTypes.func.isRequired,
  /** Triggered when a query parameter is changed */
  onQueryParamChange: PropTypes.func.isRequired,
  /** Plans trip with current settings when clicked */
  onPlanTrip: PropTypes.func.isRequired,
  /** Navigates back to trip Planner when clicked */
  onSaveSettings: PropTypes.func.isRequired,
};

export default DateTimeSelector;
